/**
 * Will-Call (local Bridge Club) Cohort Analytics
 *
 * Renders the payload from GET /admin/bridge-club/will-call/analytics/ —
 * which Laura assembles from POSTransaction + BridgeClubPurchase across
 * every local_access=True member. Surfaces:
 *   - 30-day vs prior-30-day POS spend growth
 *   - At-risk (>90 days since last visit) and rising-star buckets
 *   - Top products in the cohort, plus a competitor-line counter
 *     (Diamond Naturals, Purina, Nutrena, etc. — non-RB brands appearing
 *     on the till receipts of local members, surfaced as a "leakage"
 *     signal worth attention)
 *   - Points distribution — how many members are sitting on $10+ in
 *     unredeemed store credit
 *
 * Each member row in the at-risk / rising-stars tables is clickable into
 * the existing BridgeClubMemberDetail page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface MemberRow {
  member_id: number;
  name: string;
  last_visit?: string;
  days_since?: number;
  total_spend?: number;
  prior_30d_spend?: number;
  recent_30d_spend?: number;
  growth_pct?: number;
}

interface TopProduct {
  product_name: string;
  visits: number;
  spend: number;
}

interface PointsDistribution {
  members_with_unredeemed_credit: number;
  total_unredeemed_points: number;
  total_unredeemed_credit_dollars: number;
}

interface WillCallAnalyticsData {
  cohort: string;
  member_count: number;
  recent_30d_pos_spend: number;
  prior_30d_pos_spend: number;
  spend_growth_pct: number | null;
  churn_risk: MemberRow[];
  churn_risk_count: number;
  rising_stars: MemberRow[];
  rising_stars_count: number;
  top_products: TopProduct[];
  competitor_line_count: number;
  points_distribution: PointsDistribution;
}

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v || 0);

const WillCallAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<WillCallAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<WillCallAnalyticsData>('/admin/bridge-club/will-call/analytics/');
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24 }}>Loading will-call cohort analytics…</div>;
  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <button onClick={() => navigate(-1)} style={{ marginBottom: 12, background: 'transparent', border: 'none', color: '#2D5A27', cursor: 'pointer' }}>← Back</button>
        <div style={{ color: '#B91C1C' }}>{error || 'Not found'}</div>
      </div>
    );
  }

  const growthArrow = data.spend_growth_pct == null
    ? '—'
    : data.spend_growth_pct >= 0
      ? `↑ ${data.spend_growth_pct.toFixed(1)}%`
      : `↓ ${Math.abs(data.spend_growth_pct).toFixed(1)}%`;
  const growthColor = data.spend_growth_pct == null
    ? '#6B7280'
    : data.spend_growth_pct >= 0
      ? '#166534'
      : '#B91C1C';

  return (
    <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/admin/bridge-club')}
        style={{ marginBottom: 16, background: 'transparent', border: 'none', color: '#2D5A27', cursor: 'pointer' }}
      >
        ← Back to Bridge Club
      </button>

      <h1 style={{ marginBottom: 4 }}>Will-Call Cohort Analytics</h1>
      <div style={{ color: '#6B7280', marginBottom: 24 }}>
        {data.member_count} local Bridge Club members · POS + online activity merged
      </div>

      {/* Top stats row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>30-day POS spend</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#166534' }}>{fmtCurrency(data.recent_30d_pos_spend)}</div>
          <div style={{ fontSize: 12, color: growthColor, marginTop: 2 }}>{growthArrow} vs prior 30d</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Churn-risk members</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#B91C1C' }}>{data.churn_risk_count}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{'>'} 90 days inactive</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Rising stars</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#166534' }}>{data.rising_stars_count}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>≥50% growth recent vs prior 30d</div>
        </div>
        <div style={{ background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#92400E' }}>Unredeemed credit</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#92400E' }}>
            ${data.points_distribution.total_unredeemed_credit_dollars}
          </div>
          <div style={{ fontSize: 12, color: '#92400E', marginTop: 2 }}>
            across {data.points_distribution.members_with_unredeemed_credit} member{data.points_distribution.members_with_unredeemed_credit === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      {/* Competitor-leakage flag */}
      {data.competitor_line_count > 0 && (
        <div style={{
          background: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: 8,
          padding: 12,
          marginBottom: 24,
          color: '#7F1D1D',
        }}>
          <strong>{data.competitor_line_count} competitor line item{data.competitor_line_count === 1 ? '' : 's'}</strong>{' '}
          appeared on this cohort's POS receipts (Diamond/Purina/Nutrena/etc.) — possible
          revenue leakage to brands we don't make.
        </div>
      )}

      {/* Two-column member tables */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
        gap: 16,
        marginBottom: 24,
      }}>
        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          <h3 style={{ margin: 0, padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            Churn risk
          </h3>
          {data.churn_risk.length === 0 ? (
            <div style={{ padding: 16, color: '#6B7280' }}>No members at risk.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Member</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Days idle</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Lifetime</th>
                </tr>
              </thead>
              <tbody>
                {data.churn_risk.map((m) => (
                  <tr
                    key={m.member_id}
                    onClick={() => navigate(`/admin/bridge-club/members/${m.member_id}`)}
                    style={{ borderTop: '1px solid #E5E7EB', cursor: 'pointer' }}
                  >
                    <td style={{ padding: 8, color: '#2D5A27' }}>{m.name}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#B91C1C', fontWeight: 600 }}>{m.days_since}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{fmtCurrency(m.total_spend || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          <h3 style={{ margin: 0, padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            Rising stars
          </h3>
          {data.rising_stars.length === 0 ? (
            <div style={{ padding: 16, color: '#6B7280' }}>No standouts this period.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Member</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Prior 30d</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Recent 30d</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Growth</th>
                </tr>
              </thead>
              <tbody>
                {data.rising_stars.map((m) => (
                  <tr
                    key={m.member_id}
                    onClick={() => navigate(`/admin/bridge-club/members/${m.member_id}`)}
                    style={{ borderTop: '1px solid #E5E7EB', cursor: 'pointer' }}
                  >
                    <td style={{ padding: 8, color: '#2D5A27' }}>{m.name}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{fmtCurrency(m.prior_30d_spend || 0)}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{fmtCurrency(m.recent_30d_spend || 0)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#166534', fontWeight: 600 }}>
                      {(m.growth_pct || 0).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Top products */}
      <h3 style={{ marginBottom: 12 }}>Top products in this cohort</h3>
      {data.top_products.length === 0 ? (
        <div style={{ color: '#6B7280' }}>No POS line-item data yet.</div>
      ) : (
        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>#</th>
                <th style={{ padding: 8 }}>Product</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Visits</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Cohort spend</th>
              </tr>
            </thead>
            <tbody>
              {data.top_products.map((p, idx) => (
                <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                  <td style={{ padding: 8, color: '#6B7280' }}>#{idx + 1}</td>
                  <td style={{ padding: 8, fontWeight: 500 }}>{p.product_name}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{p.visits}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(p.spend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WillCallAnalytics;
